import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {apiV2} from "@/api/axiosConfig";
import {PaginatedResponse} from "@/types/PaginatedResponse";
import {SearchInput} from "@/components/search-input";
import {useQuery} from "react-query";
import {useDebounce} from "use-debounce";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";

type Client = {
    id: string;
    name: string;
}

const fetchClients = async (search: string): Promise<Client[]> => {
    const response = await apiV2.get<PaginatedResponse<Client>>("clients", {
        params: {
            page: 1,
            limit: 50,
            search: search || undefined,
        }
    });
    return response.data.items || [];
};

export const ClientFilter: React.FC<ColumnFilterProps> = ({label, onChange, filterParams}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebounce(search, 300);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 300);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Initialize from filterParams if present
    const getInitialClient = useCallback((): Client | null => {
        if (filterParams.clientID && filterParams.clientName) {
            return {
                id: filterParams.clientID as string,
                name: filterParams.clientName as string
            };
        }
        return null;
    }, [filterParams.clientID, filterParams.clientName]);

    // Applied state (what's actually in the filter)
    const [appliedClient, setAppliedClient] = useState<Client | null>(getInitialClient);

    // Staged state (what the user is selecting)
    const [selectedClient, setSelectedClient] = useState<Client | null>(getInitialClient);

    // Use react-query to fetch and cache clients
    const {data: clients = [], isLoading: loading} = useQuery(
        ["clients", debouncedSearch],
        () => fetchClients(debouncedSearch),
        {
            enabled: isOpen, // Only fetch when dropdown is open
            staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
            cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
        }
    );

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) {
            // When opening, sync staged state with applied state
            setSelectedClient(appliedClient);
        }
        setIsOpen(!isOpen);
    }, [isOpen, appliedClient]);

    const handleSelectClient = useCallback((client: Client | null, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedClient(client);
    }, []);

    const handleApply = useCallback(() => {
        setAppliedClient(selectedClient);
        if (selectedClient) {
            onChange({clientID: selectedClient.id, clientName: selectedClient.name});
        } else {
            onChange({clientID: undefined, clientName: undefined});
        }
        setIsOpen(false);
        setSearch("");
    }, [selectedClient, onChange]);

    const handleClear = useCallback(() => {
        setSelectedClient(null);
        setAppliedClient(null);
        onChange({clientID: undefined, clientName: undefined});
    }, [onChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearch("");
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 0);
        }
    }, [isOpen]);

    // Update state when filterParams change (e.g., from URL)
    useEffect(() => {
        const newClient = filterParams.clientID && filterParams.clientName
            ? {
                id: filterParams.clientID as string,
                name: filterParams.clientName as string
            }
            : null;

        setAppliedClient(newClient);
        setSelectedClient(newClient);
    }, [filterParams.clientID, filterParams.clientName]);

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                {appliedClient && (
                    <span className="text-xs text-blue-600 font-normal">
                        ({appliedClient.name})
                    </span>
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute ${position} mt-1 bg-white rounded shadow-lg z-50 w-[350px] flex flex-col`} onClick={(e) => e.stopPropagation()}>
                    <div className="p-2 s-gray-200">
                        <SearchInput
                            ref={searchInputRef}
                            initialValue={search}
                            onChange={setSearch}
                        />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                        <button
                            onClick={(e) => handleSelectClient(null, e)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 font-normal flex items-center gap-2"
                        >
                            <input
                                type="checkbox"
                                checked={!selectedClient}
                                onChange={() => {}}
                                className="cursor-pointer w-4 h-4"
                            />
                            <span>All</span>
                        </button>
                        {loading ? (
                            <div className="px-4 py-2 text-gray-500 text-center font-normal">
                                Loading...
                            </div>
                        ) : clients.length === 0 ? (
                            <div className="px-4 py-2 text-gray-500 text-center font-normal">
                                No results found
                            </div>
                        ) : (
                            clients.map((client) => (
                                <button
                                    key={client.id}
                                    onClick={(e) => handleSelectClient(client, e)}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 font-normal flex items-center gap-2"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedClient?.id === client.id}
                                        onChange={() => {}}
                                        className="cursor-pointer w-4 h-4"
                                    />
                                    <span>{client.name}</span>
                                </button>
                            ))
                        )}
                    </div>
                    <div className="p-3 flex gap-2">
                        <div className="flex-1">
                            <ButtonV2 label="Clear" color="gray" onClick={handleClear} />
                        </div>
                        <div className="flex-1">
                            <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
